import {useEffect} from 'react';
import {Form, Input, Select, Switch, InputNumber, Button} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {ADD_BROKER, UPDATE_BROKER} from 'graphql/mutations/stocks';
import {toast} from 'react-toastify';
import {assetInstrumentTypes} from 'pages/AssetManagement/modules/constants';
import {FormCard, spans} from 'pages/AssetManagement/v2/FormCard';
import SubmitButton from 'components/Startup/components/SubmitButton';
import DBModal from 'components/modal/modal';
import {orderFieldMappings} from 'utils/constants';
import {useVenueOptions} from '../brokerVenues';
import styles from './brokerModal.module.scss';

const {Option} = Select;

// Mirrors investing's BrokerEmailPurpose. One address can carry several.
export const BROKER_EMAIL_PURPOSES = [
  {value: 'NAV_BULLETIN', label: 'NAV bulletins (sender)'},
  {value: 'NAV_REQUEST', label: 'NAV request (to)'},
  {value: 'NAV_REQUEST_CC', label: 'NAV request (cc)'},
  {value: 'ESCALATION', label: 'Escalation'},
  {value: 'ORDERS', label: 'Orders'},
  {value: 'STATEMENTS', label: 'Statements'},
  {value: 'GENERAL', label: 'General'},
];

const blankEmailRow = () => ({email: '', label: '', purposes: ['GENERAL']});

// Old records have only the single legacy `email`; show it as one General row.
const emailRowsOf = broker => {
  if (broker?.emails?.length) {
    return broker.emails.map(({email, label, purposes}) => ({
      email,
      label: label || '',
      purposes: purposes?.length ? purposes : ['GENERAL'],
    }));
  }
  if (broker?.email) {
    return [{email: broker.email, label: 'General', purposes: ['GENERAL']}];
  }
  return [blankEmailRow()];
};

const normaliseEmailRows = rows =>
  (rows || [])
    .filter(row => row?.email?.trim())
    .map(({email, label, purposes}) => ({
      email: email.trim().toLowerCase(),
      label: label?.trim() || null,
      purposes: purposes?.length ? purposes : ['GENERAL'],
    }));

export const BrokerModal = ({isOpen, onClose, brokerData, refetch}) => {
  const [form] = Form.useForm();
  const isEditMode = !!brokerData;

  const {venueOptions, loadingVenues} = useVenueOptions(
    brokerData?.exchangeCode,
  );
  const exchangeCode = Form.useWatch('exchangeCode', form);
  // Fund managers are carried only to be emailed: no orders, so no venue,
  // account or reporting links are required of them.
  const routable = Form.useWatch('routable', form) !== false;
  const requireForRouting = routable && !isEditMode;
  const selectedVenue = venueOptions.find(venue => venue.code === exchangeCode);

  // Settlement currency follows the venue rather than being typed, so it can
  // never disagree with the exchange's own base currency — on a fresh pick and
  // on an existing broker whose stored currency has drifted from its venue.
  useEffect(() => {
    // Until the exchanges are in, or for a venue no longer on the list, there
    // is nothing to reconcile against — leave what is stored.
    if (!isOpen || !exchangeCode || loadingVenues) return;
    if (!selectedVenue || selectedVenue.unknown) return;
    const venueCurrency = selectedVenue.currency ?? undefined;
    if (form.getFieldValue('currency') !== venueCurrency) {
      form.setFieldsValue({currency: venueCurrency});
    }
  }, [isOpen, exchangeCode, loadingVenues, selectedVenue]);

  const [addBroker, {loading: addLoading}] = useMutation(ADD_BROKER, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const [updateBroker, {loading: updateLoading}] = useMutation(UPDATE_BROKER, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && brokerData) {
        // Load broker data into form for editing
        form.setFieldsValue({
          name: brokerData.name,
          emails: emailRowsOf(brokerData),
          routable: brokerData.routable !== false,
          assetClass: brokerData.assetClass,
          exchangeCode: brokerData.exchangeCode,
          currency: brokerData.currency,
          accountNumber: brokerData.accountNumber,
          enabled: brokerData.enabled,
          spreadsheetId: brokerData.spreadsheetId,
          tradeSheetName: brokerData.tradeSheetName,
          recordedTradeSheetName: brokerData.recordedTradeSheetName,
          orderSheetName: brokerData.orderSheetName,
          historyUrl: brokerData.historyUrl,
          cashUrl: brokerData.cashUrl,
          portfolioUrl: brokerData.portfolioUrl,
          statementUrl: brokerData.statementUrl,
          bundle: brokerData.bundle,
          commission: brokerData.commission,
          custodyfee: brokerData.custodyfee,
        });
      } else {
        // Reset form for new broker
        form.resetFields();
        form.setFieldsValue({
          enabled: true, // Default value
          bundle: false,
          routable: true,
          emails: [blankEmailRow()],
        });
      }
    }
  }, [isOpen, isEditMode, brokerData, form]);

  const handleSubmit = values => {
    const emails = normaliseEmailRows(values.emails);
    const isRoutable = values.routable !== false;
    const input = {
      name: values.name,
      emails,
      // Deprecated single address, kept for an investing that predates `emails`.
      email: emails[0]?.email,
      routable: isRoutable,
      assetClass: values.assetClass,
      exchangeCode: values.exchangeCode,
      currency: values.currency,
      accountNumber: values.accountNumber,
      enabled: values.enabled ?? false,
      spreadsheetId: values.spreadsheetId,
      tradeSheetName: values.tradeSheetName,
      recordedTradeSheetName: values.recordedTradeSheetName,
      orderSheetName: values.orderSheetName,
      // Required by AddBrokerInput; a fund manager charges nothing through us.
      commission: values.commission ?? (isRoutable ? undefined : 0),
      custodyfee: values.custodyfee ?? (isRoutable ? undefined : 0),
      cashUrl: values.cashUrl,
      portfolioUrl: values.portfolioUrl,
      historyUrl: values.historyUrl,
      statementUrl: values.statementUrl,
      bundle: values.bundle ?? false,
      orderFieldMappings: isEditMode
        ? brokerData.orderFieldMappings
        : orderFieldMappings,
    };

    if (isEditMode) {
      // Update broker
      updateBroker({
        variables: {
          input: {
            ...input,
            id: brokerData.id,
          },
        },
      })
        .then(({data}) => {
          if (data?.updateBroker?.__typename === 'Error') {
            toast.error(data.updateBroker.message);
          } else {
            toast.success(
              data?.updateBroker?.message || 'Broker updated successfully',
            );
            form.resetFields();
            onClose();
            if (refetch) refetch();
          }
        })
        .catch(error => {
          toast.error(error.message || 'Something went wrong');
        });
    } else {
      // Add broker
      addBroker({
        variables: {
          input,
        },
      })
        .then(({data}) => {
          if (data?.addBroker?.__typename === 'Error') {
            return toast.error(data.addBroker.message);
          } else {
            toast.success(
              data?.addBroker?.message || 'Broker added successfully',
            );
            form.resetFields();
            onClose();
            if (refetch) refetch();
          }
        })
        .catch(error => {
          toast.error(error.message || 'Something went wrong');
        });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <DBModal
      isOpen={isOpen}
      handleClose={handleCancel}
      width={840}
      content={
        <div className={styles.shell}>
          <header className={styles.head}>
            <h2>{isEditMode ? 'Edit broker' : 'Add broker'}</h2>
            <p>
              A broker record serves one exchange. A broker that trades a second
              venue needs a second record.
            </p>
          </header>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFinishFailed={() => {
              toast.error('Please fill in all required fields');
            }}>
            <div className={styles.body}>
              <FormCard
                title="Identity"
                description="Who the broker is, and every address automation emails — each tagged with what it is for">
                <div className={spans.half}>
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[{required: true, message: 'Name is required'}]}>
                    <Input placeholder="Enter broker name" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item
                    label="Routable (receives orders)"
                    name="routable"
                    valuePropName="checked"
                    extra="Off for fund managers: they never receive orders">
                    <Switch />
                  </Form.Item>
                </div>
                <div className={spans.full}>
                  <Form.List
                    name="emails"
                    rules={[
                      {
                        validator: async (_, rows) => {
                          const filled = (rows || []).filter(row =>
                            row?.email?.trim(),
                          );
                          if (!filled.length) {
                            throw new Error('At least one email is required');
                          }
                          const seen = {};
                          for (const row of filled) {
                            const email = row.email.trim().toLowerCase();
                            if (seen[email]) {
                              throw new Error(`${email} is listed twice`);
                            }
                            seen[email] = true;
                          }
                        },
                      },
                    ]}>
                    {(fields, {add, remove}, {errors}) => (
                      <>
                        {fields.map(({key, name, ...rest}) => (
                          <div key={key} className={styles.emailRow}>
                            <Form.Item
                              {...rest}
                              className={styles.emailCol}
                              label={name === 0 ? 'Email' : undefined}
                              name={[name, 'email']}
                              rules={[
                                {required: true, message: 'Email is required'},
                                {
                                  type: 'email',
                                  message: 'Please enter a valid email',
                                },
                              ]}>
                              <Input placeholder="name@broker.com" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              className={styles.labelCol}
                              label={name === 0 ? 'Label' : undefined}
                              name={[name, 'label']}>
                              <Input placeholder="What it is for" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              className={styles.purposeCol}
                              label={name === 0 ? 'Purposes' : undefined}
                              name={[name, 'purposes']}>
                              <Select
                                mode="multiple"
                                allowClear
                                placeholder="General"
                                options={BROKER_EMAIL_PURPOSES}
                              />
                            </Form.Item>
                            <Button
                              type="text"
                              danger
                              aria-label="Remove email"
                              icon={<DeleteOutlined />}
                              disabled={fields.length === 1}
                              onClick={() => remove(name)}
                              className={
                                name === 0
                                  ? styles.removeFirst
                                  : styles.removeRow
                              }
                            />
                          </div>
                        ))}
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => add(blankEmailRow())}>
                          Add email
                        </Button>
                        <Form.ErrorList errors={errors} />
                      </>
                    )}
                  </Form.List>
                </div>
              </FormCard>

              <FormCard
                title="Routing"
                description="Which orders can reach this broker. An order is only ever assigned a broker whose exchange matches the instrument's.">
                <div className={spans.third}>
                  <Form.Item
                    label="Asset Class"
                    name="assetClass"
                    rules={[
                      {required: true, message: 'Asset class is required'},
                    ]}>
                    <Select placeholder="Select asset class">
                      {assetInstrumentTypes.map(item => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className={spans.third}>
                  <Form.Item
                    label="Exchange"
                    name="exchangeCode"
                    extra="A broker with no exchange is never assigned an order."
                    rules={[
                      {required: routable, message: 'An exchange is required'},
                    ]}>
                    <Select
                      showSearch
                      optionFilterProp="children"
                      loading={loadingVenues}
                      placeholder="Select exchange">
                      {venueOptions.map(venue => (
                        <Option key={venue.code} value={venue.code}>
                          {venue.code} — {venue.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className={spans.third}>
                  <Form.Item
                    label="Settlement Currency"
                    name="currency"
                    extra={
                      exchangeCode && !selectedVenue?.currency
                        ? 'This exchange has no base currency set. Set one on the exchange first.'
                        : "Follows the exchange's base currency."
                    }
                    rules={[
                      {
                        required: routable,
                        message: 'Select an exchange with a base currency',
                      },
                    ]}>
                    <Input disabled placeholder="Set by the exchange" />
                  </Form.Item>
                </div>
              </FormCard>

              <FormCard
                title="Account and fees"
                description="What the broker charges and which account settles">
                <div className={spans.third}>
                  <Form.Item
                    label="Account Number"
                    name="accountNumber"
                    rules={[
                      {
                        required: requireForRouting,
                        message: 'Account number is required',
                      },
                    ]}>
                    <Input placeholder="Enter account number" />
                  </Form.Item>
                </div>
                <div className={spans.third}>
                  <Form.Item
                    rules={[
                      {
                        required: requireForRouting,
                        message: 'Commission is required',
                      },
                    ]}
                    label="Commission"
                    name="commission">
                    <InputNumber
                      min={0}
                      placeholder="Enter commission"
                      className={styles.fullWidth}
                    />
                  </Form.Item>
                </div>
                <div className={spans.third}>
                  <Form.Item
                    rules={[
                      {
                        required: requireForRouting,
                        message: 'Custody fee is required',
                      },
                    ]}
                    label="Custody Fee"
                    name="custodyfee">
                    <InputNumber
                      min={0}
                      placeholder="Enter custody fee"
                      className={styles.fullWidth}
                    />
                  </Form.Item>
                </div>
              </FormCard>

              <FormCard
                title="Order sheets"
                description="The Google Sheet orders are written to and read back from">
                <div className={spans.half}>
                  <Form.Item label="Spreadsheet ID" name="spreadsheetId">
                    <Input placeholder="Enter spreadsheet ID" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item label="Order Sheet Name" name="orderSheetName">
                    <Input placeholder="Enter order sheet name" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item label="Trade Sheet Name" name="tradeSheetName">
                    <Input placeholder="Enter trade sheet name" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item
                    label="Recorded Trade Sheet Name"
                    name="recordedTradeSheetName">
                    <Input placeholder="Enter recorded trade sheet name" />
                  </Form.Item>
                </div>
              </FormCard>

              <FormCard
                title="Reporting links"
                description="Where admin pulls this broker's statements and balances from">
                <div className={spans.half}>
                  <Form.Item
                    rules={[
                      {
                        required: requireForRouting,
                        message: 'History URL is required',
                      },
                      {
                        type: 'url',
                        message: 'Please enter a valid URL',
                      },
                    ]}
                    label="History URL"
                    name="historyUrl">
                    <Input placeholder="Enter history URL" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item
                    rules={[
                      {
                        required: requireForRouting,
                        message: 'Statement URL is required',
                      },
                      {
                        type: 'url',
                        message: 'Please enter a valid URL',
                      },
                    ]}
                    label="Statement URL"
                    name="statementUrl">
                    <Input placeholder="Enter statement URL" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item
                    rules={[
                      {
                        type: 'url',
                        message: 'Please enter a valid URL',
                      },
                    ]}
                    label="Cash Balance URL"
                    name="cashUrl">
                    <Input placeholder="Enter cash URL" />
                  </Form.Item>
                </div>
                <div className={spans.half}>
                  <Form.Item
                    rules={[
                      {
                        type: 'url',
                        message: 'Please enter a valid URL',
                      },
                    ]}
                    label="Portfolio URL"
                    name="portfolioUrl">
                    <Input placeholder="Enter portfolio URL" />
                  </Form.Item>
                </div>
              </FormCard>

              <FormCard
                title="Availability"
                description="Whether the broker is in use, and whether its orders are bundled">
                <div className={spans.third}>
                  <Form.Item
                    label="Enabled"
                    name="enabled"
                    valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </div>
                <div className={spans.third}>
                  {/* Without valuePropName the switch is handed `value`, so an
                      existing broker's bundle setting rendered as off. */}
                  <Form.Item
                    label="Bundle"
                    name="bundle"
                    valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </div>
              </FormCard>
            </div>

            <div className={styles.foot}>
              <SubmitButton
                secondary
                type="button"
                label="Cancel"
                handleClick={handleCancel}
              />
              <SubmitButton
                label={isEditMode ? 'Update' : 'Create'}
                disabled={addLoading || updateLoading}
              />
            </div>
          </Form>
        </div>
      }
    />
  );
};
